import { useEffect, useState } from "react";
import { DeviceMobile, X, DownloadSimple } from "@phosphor-icons/react";

const DISMISS_KEY = "rv_install_dismissed";

// Custom PWA "Add to Home Screen" banner. Appears once the user has watched their first
// video (rv_watched_once) and the browser has offered install (beforeinstallprompt).
export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return undefined;
    const deferredRef = { current: null };

    const onBIP = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferred(e);
      if (localStorage.getItem("rv_watched_once") === "1") setVisible(true);
    };
    const onWatched = () => {
      if (deferredRef.current && localStorage.getItem(DISMISS_KEY) !== "1") setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("rv-watched", onWatched);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("rv-watched", onWatched);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!visible || !deferred) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-6 sm:w-[380px] z-[90] rv-fade"
    >
      <div className="flex items-center gap-4 rounded-2xl border border-amber/30 bg-[#0A0A0A]/95 backdrop-blur-xl p-4 shadow-2xl shadow-black/60">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber/15 border border-amber/30">
          <DeviceMobile size={22} weight="duotone" className="text-amber" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-[#EDEDED] leading-tight">Install ReelVault</p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-white/40">
            One-tap access, right on your home screen
          </p>
        </div>
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="flex items-center gap-1.5 rounded-full bg-amber px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-black font-bold hover:bg-amber/80 transition-colors duration-300"
        >
          <DownloadSimple size={14} weight="bold" /> Install
        </button>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-white/40 hover:text-white transition-colors duration-300"
        >
          <X size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}
